import { BaseClass, BaseClassConstructor, Data, Enumerable, field, primaryKey, queryOnly } from '@dimandzhi/graphql';
import _ from 'lodash';
import "reflect-metadata";
import { MediaType } from './m/scheduler/internal-schedule-class';

export namespace GraphQL {

  export namespace ASR {

    export class LangModel extends BaseClassConstructor<LangModel>('Lang')() {
      @primaryKey() id?: number;
      @field() lang?: string;
      @field() comment?: string;
      @field() createdAt?: string;
    }

  }

  export const ChannelTypes = Object.seal({
    TV: 'TV',
    RADIO: 'RADIO'
  });

  export class Channel extends BaseClassConstructor<Channel>('Channel')() {
    @primaryKey() id?: number;
    @field() name?: string;
    @field({ enumValues: ChannelTypes }) type?: Enumerable;
    @field() stationSourceID?: number | null;
    @field() exportEveryHour?: boolean;
  }

  export class Server extends BaseClassConstructor<Server>('Server')() {
    @primaryKey() id?: number;
    @field() urlBase?: string;
    @field() fsPath?: string;
    @field() sshPrivateKey?: string;
    @field() sshConfig?: string;
    @queryOnly() channelStreamCount?: number;
  }

  export class ChannelStream extends BaseClassConstructor<ChannelStream>('ChannelStream')() {
    @primaryKey() id?: number;
    @field() Channel?: Channel;
    @field() order?: number;
    @field() reference?: string;
    @field() Server: Server;
    @field() asrFrom?: string;
    @field() useScheduleFromChannel?: boolean;
    @queryOnly() fragmentCount?: number;
    @queryOnly() replacedAt?: string | null;
    @queryOnly() replacedWith?: number | null;
    @queryOnly() isReferencedByReplacedStream?: boolean | null;
    @field() comment?: string | null;
  }

  export class Fragment extends BaseClassConstructor<Fragment>('Fragment')() {
    @primaryKey() id?: number;
    @field() ChannelStream?: ChannelStream;
    @field() start?: string;
    @field() duration?: number;
    @field() fileName?: string;
    // @field() words: Word[] | null;
    @queryOnly() wordsNotNull?: boolean;
    @field() task?: number | null;
    @field() failCause?: string[] | null;
    @field() ignoreProblems?: boolean;
  }

  export class Group extends BaseClassConstructor<Group>('Group')() {
    @field({ primaryKey: true }) id: number;
    @field() name: string;
    @field() asrPriorityOffset: number;
  }

  export type UserSettings = {
    [key: string]: any; // allowed old/untracked settings
    fragmentsWithProblems?: {
      maxFails: number;
      minDuration: number;
      maxDuration: number;
    };
  };

  export class User extends BaseClassConstructor<User>('User')() {
    @primaryKey() id: number;
    @field() Group: Group;
    @field() settings: UserSettings;
  }

  class mergedScheduleBaseClass extends BaseClass {
    @field() mediaID: number; // hooked in mergedScheduleClassConstructor to address MediaType field
    @field() start: string; // in UTC
    @field() duration: number; // as seconds
    @field() langModelID: number;
    @field() spleeter: number;
    @field() groupID: number;
  }

  class mergedScheduleForDateClass extends mergedScheduleBaseClass { }
  class mergedScheduleForPeriodClass extends mergedScheduleBaseClass {
    asrPriority: number;
    dynamicASRPriorityFromDate: boolean;
  }
  export enum TimeWindow {
    ForDate = 'ForDate',
    ForPeriod = 'ForPeriod',
  }
  type mergedScheduleForTimeWindowClass<T = TimeWindow> = T extends TimeWindow.ForPeriod
    ? typeof mergedScheduleForPeriodClass
    : T extends TimeWindow.ForDate
    ? typeof mergedScheduleForDateClass
    : never;

  export const mergedScheduleClassConstructor = <
    ThisMediaType extends MediaType,
    ThisTimeWindow extends TimeWindow,
    R = mergedScheduleForTimeWindowClass<ThisTimeWindow>,
  >(
    mediaType: ThisMediaType,
    timeWindow: ThisTimeWindow,
  ): R => {
    function mergedSchedule() {
      this.prototype = BaseClassConstructor('Schedule');
      return Object.defineProperty(this, 'mediaID', {
        get: () => _.get(this, mediaType),
        set: (value: any): void => _.set(this, mediaType, value),
        enumerable: true, // yes, like a field
        configurable: true, // nice to have
      });
    }
    mergedSchedule.prototype.primaryKeys = function () {
      return [];
    };
    return mergedSchedule as any as R;
  };

  export class MergedChannelScheduleForDate extends mergedScheduleClassConstructor(
    MediaType.Channel,
    TimeWindow.ForDate,
  ) { }
  export class MergedChannelScheduleForPeriod extends mergedScheduleClassConstructor(
    MediaType.Channel,
    TimeWindow.ForPeriod,
  ) { }
  export class MergedChannelStreamScheduleForDate extends mergedScheduleClassConstructor(
    MediaType.ChannelStream,
    TimeWindow.ForDate,
  ) { }
  export class MergedChannelStreamScheduleForPeriod extends mergedScheduleClassConstructor(
    MediaType.ChannelStream,
    TimeWindow.ForPeriod,
  ) { }

  export class Schedule extends BaseClassConstructor<Schedule>('Schedule')() {
    @primaryKey() id: number;
    @field() channelID: number | null;
    @field() channelStreamID: number | null;
    @field() Group: Group;
    @field() langModelID: number;
    @field() date: string | null;
    @field() dow: number[] | null;
    @field() time: string;
    @field() timezone: string;
    @field() duration: number;
    @field() spleeter: number;
    @field() title: string;
    @field() split: boolean;
  }

  export class ChannelASRPriotiyOffset extends BaseClassConstructor<ChannelASRPriotiyOffset>(
    'ChannelASRPriotiyOffset',
  )() {
    @primaryKey() id: number;
    @field() Channel: Channel;
    @field() Group: Group;
    @field() asrPriorityOffset: number;
  }

  export namespace UserSchema {

    export class UserSelf extends BaseClassConstructor('User')() {
      @primaryKey() id: number;
      @field('group') group: {
        id: number;
        name: string;
      };
      @field() settings: UserSettings;

      static getFromData(data: Data) {
        return _.get(data, ['resp', 'data', this.getModelName(), 'self']) as UserSelf | undefined;
      }
    }

    export class Schedule extends BaseClassConstructor('Schedule')() {
      @primaryKey() id: number;
      @field() channelID: number | null;
      @field() channelStreamID: number | null;
      @field() langModelID: number;
      @field() date: string | null;
      @field() dow: number[] | null;
      @field() time: string;
      @field() timezone: string;
      @field() duration: number;
      @field() spleeter: number;
      @field() title: string;
      @field() split: boolean;
    }

    export class UserGroup extends BaseClassConstructor('UserGroup')() {
      @primaryKey() id: number;
      @field() name: string;
    }

    export class UserInGroup extends BaseClassConstructor('User')() {
      @primaryKey() id: number;
      @field() firstName: string;
      @field() lastName: string;
      @field() email: string;
      @field() enabled: boolean;
      @field() isManager: boolean;
    }

  }

}
